import { useEffect, useState } from 'react';
import { ChevronRight, ChevronLeft, FileText, Download, RotateCcw, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import { PRO_SERVICES, SERVICE_SLUG_TO_NAV_ID } from '../../types/professional';
import type { AstrologerClient, AstrologerAnalysis } from '../../types/professional';
import { useToast } from '../../components/ui/ToastSystem';
import { getSignEmoji } from '../../lib/astrology';
import { useNavMenu } from '../../hooks/useNavMenu';

const FORECAST_PERIODS = [
  { value: 'mes-atual',    label: 'Este mês' },
  { value: '3-meses',      label: '3 meses' },
  { value: '6-meses',      label: '6 meses' },
  { value: 'proximo-ano',  label: '1 ano' },
];

const TAROT_SPREADS: Record<number, string> = {
  1:  'Carta do Dia',
  3:  'Passado · Presente · Futuro',
  7:  'Ferradura',
  10: 'Cruz Celta',
};

const LENORMAND_SPREADS: Record<number, string> = {
  1: 'Carta do Dia',
  3: 'Presente · Futuro · Conselho',
  5: 'Pentágono',
  9: 'Grand Tableau',
};

const CATEGORIES = [
  'Análise Natal',
  'Previsões',
  'Relacionamentos',
  'Oráculos',
  'Jornadas',
  'Números e Ciclos',
];

function sanitizeGenerationError(raw: string | null | undefined): string {
  if (!raw) return 'Não foi possível gerar a análise. Tente novamente.';
  const lower = raw.toLowerCase();
  if (
    lower.includes('credit') || lower.includes('billing') ||
    lower.includes('quota') || lower.includes('too low') ||
    lower.includes('resource_exhausted') || lower.includes('insufficient_quota')
  ) return 'Serviço de IA temporariamente indisponível. Verifique os créditos da API e tente novamente.';
  if (lower.includes('timeout') || lower.includes('timed out'))
    return 'A geração demorou mais que o esperado. Tente novamente.';
  if (raw.includes('{') || raw.includes('Error:') || raw.length > 150)
    return 'Não foi possível gerar a análise. Tente novamente.';
  return raw;
}

function clientLabel(c: AstrologerClient) {
  const sign = c.natal_chart_json?.sun_sign ?? '';
  return sign ? `${c.name}, ${getSignEmoji(sign)} ${sign}` : c.name;
}

export function ProfessionalGenerateAnalysis() {
  const { astrologerProfile } = useAstrologerProfile();
  const { showToast } = useToast();
  const navConfig = useNavMenu();

  const availableServices = navConfig === null
    ? PRO_SERVICES
    : PRO_SERVICES.filter(s => {
        const navId = SERVICE_SLUG_TO_NAV_ID[s.slug];
        if (!navId) return true;
        const cfg = navConfig.find(c => c.id === navId);
        return cfg === undefined || cfg.enabled;
      });

  const [clients, setClients] = useState<AstrologerClient[]>([]);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [clientId, setClientId] = useState('');

  // Step 2
  const [serviceSlug, setServiceSlug] = useState('');
  const [period, setPeriod] = useState('mes-atual');
  const [numCards, setNumCards] = useState(1);
  const [question, setQuestion] = useState('');
  const [cardQuestion, setCardQuestion] = useState('');
  const [client2Id, setClient2Id] = useState('');
  const [partnerMode, setPartnerMode] = useState<'client' | 'manual'>('client');
  const [partnerName, setPartnerName] = useState('');
  const [partnerBirthDate, setPartnerBirthDate] = useState('');
  const [partnerBirthCity, setPartnerBirthCity] = useState('');
  const [weekNum, setWeekNum] = useState(1);
  const [petName, setPetName] = useState('');
  const [petBirthDate, setPetBirthDate] = useState('');
  const [cityInput, setCityInput] = useState('');

  // Step 3 / result
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AstrologerAnalysis | null>(null);
  const [downloading, setDownloading] = useState<'pdf' | 'docx' | null>(null);

  useEffect(() => {
    if (!astrologerProfile) return;
    supabase
      .from('astrologer_clients')
      .select('*')
      .eq('astrologer_id', astrologerProfile.id)
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => setClients(data ?? []));
  }, [astrologerProfile?.id]);

  const selectedClient = clients.find(c => c.id === clientId);
  const selectedService = PRO_SERVICES.find(s => s.slug === serviceSlug);

  function canProceedToStep2() {
    if (!selectedClient) return false;
    if (!selectedClient.birth_date) return false;
    return true;
  }

  function canGenerate() {
    if (!serviceSlug) return false;
    if (selectedService?.needsSecondClient) {
      if (partnerMode === 'client' && !client2Id) return false;
      if (partnerMode === 'manual' && (!partnerName.trim() || !partnerBirthDate)) return false;
    }
    if (selectedService?.needsQuestion && !question.trim()) return false;
    if (selectedService?.needsPetInfo && (!petName.trim() || !petBirthDate)) return false;
    if (selectedService?.needsCity && !cityInput.trim()) return false;
    return true;
  }

  async function handleGenerate() {
    if (!selectedClient || !astrologerProfile || !selectedService) return;
    setGenerating(true);
    setResult(null);

    const serviceConfig: Record<string, unknown> = {};
    if (selectedService.needsPeriod)     serviceConfig.period      = period;
    if (selectedService.cardOptions)     serviceConfig.numCards    = numCards;
    if (selectedService.needsQuestion)   serviceConfig.question    = question;
    if (selectedService.needsWeekNumber) serviceConfig.week        = weekNum;
    if (selectedService.needsCity)       serviceConfig.city        = cityInput;
    if (cardQuestion.trim())             serviceConfig.cardQuestion = cardQuestion;
    if (selectedService.needsPetInfo) {
      serviceConfig.petName      = petName;
      serviceConfig.petBirthDate = petBirthDate;
    }

    try {
      // For manual partner entry, create partner as client first
      let resolvedClient2Id = client2Id || null;
      if (selectedService.needsSecondClient && partnerMode === 'manual' && partnerName.trim()) {
        const { data: newPartner } = await supabase.from('astrologer_clients').insert({
          astrologer_id: astrologerProfile.id,
          name:          partnerName.trim(),
          birth_date:    partnerBirthDate,
          birth_city:    partnerBirthCity.trim() || null,
          is_active:     true,
          birth_timezone: 'America/Sao_Paulo',
        }).select('id').single();
        if (newPartner?.id) resolvedClient2Id = newPartner.id;
      }

      // Save pet data to client notes if provided
      if (selectedService.needsPetInfo && petName.trim()) {
        const petNote = `Pet: ${petName.trim()}${petBirthDate ? ` (nasc. ${new Date(petBirthDate + 'T00:00:00').toLocaleDateString('pt-BR')})` : ''}`;
        const currentNotes = selectedClient.notes ?? '';
        const hasPetEntry  = currentNotes.includes('Pet:');
        if (!hasPetEntry) {
          await supabase.from('astrologer_clients').update({
            notes: currentNotes ? `${currentNotes}\n${petNote}` : petNote,
          }).eq('id', selectedClient.id);
        }
      }

      const invoke = () => supabase.functions.invoke('astrologer-generate-analysis', {
        body: {
          astrologer_id:  astrologerProfile!.id,
          client_id:      selectedClient!.id,
          service_slug:   serviceSlug,
          client2_id:     resolvedClient2Id,
          service_config: serviceConfig,
        },
      });

      let { data } = await invoke();

      // Auto-retry once on any failure, catches transient provider errors
      if (!data?.ok || !data.data) {
        await new Promise(r => setTimeout(r, 2000));
        ({ data } = await invoke());
      }

      if (data?.ok && data.data) {
        setResult(data.data);
        setStep(3);
      } else {
        showToast({ message: sanitizeGenerationError(data?.error), type: 'error' });
      }
    } catch (err) {
      showToast({ message: sanitizeGenerationError((err as Error).message), type: 'error' });
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownloadDocx() {
    if (!result) return;
    setDownloading('docx');
    try {
      const { data } = await supabase.functions.invoke('astrologer-generate-docx', {
        body: { analysis_id: result.id },
      });
      if (data?.ok && data.data?.url) {
        window.open(data.data.url, '_blank', 'noopener,noreferrer');
      } else {
        showToast({ message: data?.error ?? 'Erro ao gerar Word', type: 'error' });
      }
    } catch (err) {
      showToast({ message: (err as Error).message, type: 'error' });
    } finally {
      setDownloading(null);
    }
  }

  async function handleDownloadPdf() {
    if (!result) return;
    setDownloading('pdf');
    try {
      const { data } = await supabase.functions.invoke('astrologer-generate-pdf', {
        body: { analysis_id: result.id },
      });
      if (data?.ok && data.data?.url) {
        window.open(data.data.url, '_blank', 'noopener,noreferrer');
      } else {
        showToast({ message: data?.error ?? 'Erro ao gerar PDF', type: 'error' });
      }
    } catch (err) {
      showToast({ message: (err as Error).message, type: 'error' });
    } finally {
      setDownloading(null);
    }
  }

  function handleReset() {
    setStep(1);
    setClientId('');
    setServiceSlug('');
    setPeriod('mes-atual');
    setNumCards(1);
    setQuestion('');
    setCardQuestion('');
    setClient2Id('');
    setPartnerMode('client');
    setPartnerName('');
    setPartnerBirthDate('');
    setPartnerBirthCity('');
    setWeekNum(1);
    setPetName('');
    setPetBirthDate('');
    setCityInput('');
    setResult(null);
  }

  const contentText = result?.content_json
    ? (typeof result.content_json === 'string'
        ? result.content_json
        : (result.content_json as { text?: string })?.text ?? JSON.stringify(result.content_json, null, 2))
    : '';

  return (
    <div style={{ maxWidth: '820px' }}>

      {/* Page header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-28)', fontWeight: 400, margin: '0 0 0.4rem', color: 'var(--text-primary)' }}>
          Gerar Análise
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-15)', margin: 0 }}>
          Gere análises personalizadas para seus clientes com IA.
        </p>
      </div>

      {/* Steps indicator */}
      {step < 3 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem', fontSize: 'var(--text-13)' }}>
          {(['Cliente', 'Serviço', 'Resultado'] as const).map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done   = step > n;
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  width: '24px', height: '24px', borderRadius: '50%', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', fontWeight: 600,
                  background: active ? 'var(--petrol)' : done ? 'var(--petrol)' : 'var(--border-subtle)',
                  color: active || done ? '#fff' : 'var(--text-muted)',
                  fontSize: '12px',
                }}>
                  {n}
                </span>
                <span style={{ color: active ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: active ? 600 : 400 }}>
                  {label}
                </span>
                {i < 2 && <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Step 1: Select client ── */}
      {step === 1 && (
        <div style={{ background: 'var(--bg-surface)', borderRadius: '12px', border: '1px solid var(--border-subtle)', padding: '1.75rem', boxShadow: 'var(--shadow)' }}>
          <h2 style={{ fontSize: 'var(--text-18)', fontWeight: 600, margin: '0 0 1.25rem', color: 'var(--text-primary)' }}>
            Selecionar cliente
          </h2>

          <label style={{ display: 'block', fontSize: 'var(--text-13)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Cliente
          </label>
          <select
            className="form-input"
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            style={{ width: '100%', marginBottom: '1.5rem' }}
          >
            <option value="">Escolha um cliente...</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{clientLabel(c)}</option>
            ))}
          </select>

          {selectedClient && !selectedClient.birth_date && (
            <div style={{ background: 'rgba(180,150,0,0.1)', border: '1px solid rgba(180,150,0,0.3)', borderRadius: '8px', padding: '0.875rem 1rem', marginBottom: '1.25rem', color: 'var(--text-primary)', fontSize: 'var(--text-13)' }}>
              ⚠️ Este cliente não tem data de nascimento. Complete o cadastro antes de gerar análises.
            </div>
          )}

          {selectedClient && selectedClient.birth_date && (
            <div style={{ background: 'var(--petrol-bg)', borderRadius: '8px', padding: '0.875rem 1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <User size={18} style={{ color: 'var(--petrol)', flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-15)', color: 'var(--text-primary)' }}>{selectedClient.name}</p>
                {selectedClient.natal_chart_json?.sun_sign && (
                  <p style={{ margin: '0.2rem 0 0', fontSize: 'var(--text-13)', color: 'var(--text-secondary)' }}>
                    Sol {getSignEmoji(selectedClient.natal_chart_json.sun_sign)} {selectedClient.natal_chart_json.sun_sign}
                    {selectedClient.natal_chart_json?.moon_sign && ` · Lua ${getSignEmoji(selectedClient.natal_chart_json.moon_sign)} ${selectedClient.natal_chart_json.moon_sign}`}
                    {selectedClient.natal_chart_json?.ascendant && ` · Asc ${getSignEmoji(selectedClient.natal_chart_json.ascendant)} ${selectedClient.natal_chart_json.ascendant}`}
                  </p>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-primary"
              disabled={!canProceedToStep2()}
              onClick={() => setStep(2)}
            >
              Próximo <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Select service ── */}
      {step === 2 && (
        <div style={{ background: 'var(--bg-surface)', borderRadius: '12px', border: '1px solid var(--border-subtle)', padding: '1.75rem', boxShadow: 'var(--shadow)' }}>
          <h2 style={{ fontSize: 'var(--text-18)', fontWeight: 600, margin: '0 0 1.25rem', color: 'var(--text-primary)' }}>
            Selecionar serviço
          </h2>

          <p style={{ fontSize: 'var(--text-13)', color: 'var(--text-secondary)', margin: '0 0 1.25rem' }}>
            Cliente: <strong>{selectedClient?.name}</strong>
          </p>

          {CATEGORIES.map(cat => {
            const services = availableServices.filter(s => s.category === cat);
            if (!services.length) return null;
            return (
              <div key={cat} style={{ marginBottom: '1.25rem' }}>
                <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
                  {cat}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {services.map(svc => (
                    <label
                      key={svc.slug}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                        padding: '0.75rem 1rem', borderRadius: '8px', cursor: 'pointer',
                        border: `1px solid ${serviceSlug === svc.slug ? 'var(--petrol)' : 'var(--border-subtle)'}`,
                        background: serviceSlug === svc.slug ? 'var(--petrol-bg)' : 'transparent',
                        transition: 'all 0.12s',
                      }}
                    >
                      <input
                        type="radio"
                        name="service"
                        value={svc.slug}
                        checked={serviceSlug === svc.slug}
                        onChange={() => { setServiceSlug(svc.slug); setClient2Id(''); setCardQuestion(''); setCityInput(''); setPartnerMode('client'); setNumCards(svc.cardOptions?.[0] ?? 1); }}
                        style={{ accentColor: 'var(--petrol)', marginTop: '2px', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 'var(--text-15)', color: 'var(--text-primary)', fontWeight: serviceSlug === svc.slug ? 600 : 400 }}>
                          {svc.name}
                        </span>
                        <p style={{ margin: '0.1rem 0 0', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                          {svc.description}
                        </p>
                      </div>
                      {svc.noCache && (
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>sempre novo</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Service-specific config */}
          {selectedService?.needsPeriod && (
            <div style={{ margin: '1rem 0', padding: '1rem', background: 'var(--petrol-bg)', borderRadius: '8px' }}>
              <p style={{ fontSize: 'var(--text-13)', fontWeight: 600, margin: '0 0 0.625rem' }}>Período</p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {FORECAST_PERIODS.map(p => (
                  <button
                    key={p.value}
                    className={period === p.value ? 'btn btn-primary' : 'btn btn-ghost'}
                    style={{ padding: '0.3rem 0.875rem', fontSize: 'var(--text-13)' }}
                    onClick={() => setPeriod(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedService?.cardOptions && (
            <div style={{ margin: '1rem 0', padding: '1rem', background: 'var(--petrol-bg)', borderRadius: '8px' }}>
              <p style={{ fontSize: 'var(--text-13)', fontWeight: 600, margin: '0 0 0.625rem' }}>Tipo de tirada</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {selectedService.cardOptions.map(n => {
                  const spreadMap = serviceSlug === 'tarot' ? TAROT_SPREADS : LENORMAND_SPREADS;
                  const label = spreadMap[n] ?? `${n} cartas`;
                  return (
                    <button
                      key={n}
                      className={numCards === n ? 'btn btn-primary' : 'btn btn-ghost'}
                      style={{ padding: '0.4rem 1rem', fontSize: 'var(--text-13)', textAlign: 'left', justifyContent: 'flex-start' }}
                      onClick={() => setNumCards(n)}
                    >
                      {label} <span style={{ opacity: 0.6, marginLeft: '0.4rem' }}>({n} {n === 1 ? 'carta' : 'cartas'})</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: '0.875rem' }}>
                <label style={{ display: 'block', fontSize: 'var(--text-13)', marginBottom: '0.3rem', color: 'var(--text-secondary)' }}>
                  Pergunta (opcional)
                </label>
                <input
                  className="form-input"
                  type="text"
                  value={cardQuestion}
                  onChange={e => setCardQuestion(e.target.value)}
                  placeholder="Pergunta a ser respondida pela tirada..."
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          {selectedService?.needsQuestion && (
            <div style={{ margin: '1rem 0' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-13)', fontWeight: 600, marginBottom: '0.4rem' }}>
                Qual é a pergunta?
              </label>
              <input
                className="form-input"
                type="text"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder="Escreva a pergunta do cliente..."
                style={{ width: '100%' }}
              />
            </div>
          )}

          {selectedService?.needsSecondClient && (
            <div style={{ margin: '1rem 0', padding: '1rem', background: 'var(--petrol-bg)', borderRadius: '8px' }}>
              <p style={{ fontSize: 'var(--text-13)', fontWeight: 600, margin: '0 0 0.75rem' }}>Dados do parceiro</p>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <button
                  className={partnerMode === 'client' ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ padding: '0.3rem 0.875rem', fontSize: 'var(--text-13)' }}
                  onClick={() => setPartnerMode('client')}
                >
                  Cliente cadastrado
                </button>
                <button
                  className={partnerMode === 'manual' ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ padding: '0.3rem 0.875rem', fontSize: 'var(--text-13)' }}
                  onClick={() => setPartnerMode('manual')}
                >
                  Dados manuais
                </button>
              </div>
              {partnerMode === 'client' ? (
                <select
                  className="form-input"
                  value={client2Id}
                  onChange={e => setClient2Id(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">Selecionar cliente...</option>
                  {clients.filter(c => c.id !== clientId).map(c => (
                    <option key={c.id} value={c.id}>{clientLabel(c)}</option>
                  ))}
                </select>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 'var(--text-13)', marginBottom: '0.3rem' }}>Nome do parceiro *</label>
                    <input className="form-input" type="text" value={partnerName} onChange={e => setPartnerName(e.target.value)} placeholder="Nome completo" style={{ width: '100%' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={{ display: 'block', fontSize: 'var(--text-13)', marginBottom: '0.3rem' }}>Data de nascimento *</label>
                      <input className="form-input" type="date" value={partnerBirthDate} onChange={e => setPartnerBirthDate(e.target.value)} style={{ width: '100%' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: '150px' }}>
                      <label style={{ display: 'block', fontSize: 'var(--text-13)', marginBottom: '0.3rem' }}>Cidade de nascimento</label>
                      <input className="form-input" type="text" value={partnerBirthCity} onChange={e => setPartnerBirthCity(e.target.value)} placeholder="Ex: São Paulo" style={{ width: '100%' }} />
                    </div>
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                    O parceiro será salvo na sua lista de clientes.
                  </p>
                </div>
              )}
            </div>
          )}

          {selectedService?.needsWeekNumber && (
            <div style={{ margin: '1rem 0', padding: '1rem', background: 'var(--petrol-bg)', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-13)', fontWeight: 600, marginBottom: '0.625rem' }}>
                Semana do programa (1–12)
              </label>
              <input
                type="number"
                className="form-input"
                min={1} max={12}
                value={weekNum}
                onChange={e => setWeekNum(Number(e.target.value))}
                style={{ width: '80px' }}
              />
            </div>
          )}

          {/* I-Ching optional question */}
          {serviceSlug === 'i-ching' && (
            <div style={{ margin: '1rem 0', padding: '1rem', background: 'var(--petrol-bg)', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-13)', fontWeight: 600, marginBottom: '0.4rem' }}>
                Questão para o I Ching (opcional)
              </label>
              <input
                className="form-input"
                type="text"
                value={cardQuestion}
                onChange={e => setCardQuestion(e.target.value)}
                placeholder="Qual é a situação ou dúvida do cliente?"
                style={{ width: '100%' }}
              />
            </div>
          )}

          {/* Astrocartografia city input */}
          {selectedService?.needsCity && (
            <div style={{ margin: '1rem 0', padding: '1rem', background: 'var(--petrol-bg)', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-13)', fontWeight: 600, marginBottom: '0.4rem' }}>
                Cidade para análise *
              </label>
              <input
                className="form-input"
                type="text"
                value={cityInput}
                onChange={e => setCityInput(e.target.value)}
                placeholder="Ex: Lisboa, Portugal"
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0.3rem 0 0' }}>
                Informe a cidade que o cliente deseja analisar.
              </p>
            </div>
          )}

          {selectedService?.needsPetInfo && (
            <div style={{ margin: '1rem 0', padding: '1rem', background: 'var(--petrol-bg)', borderRadius: '8px' }}>
              <p style={{ fontSize: 'var(--text-13)', fontWeight: 600, margin: '0 0 0.75rem' }}>Dados do pet</p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <label style={{ display: 'block', fontSize: 'var(--text-13)', marginBottom: '0.3rem' }}>Nome do pet *</label>
                  <input className="form-input" type="text" value={petName} onChange={e => setPetName(e.target.value)} placeholder="Ex: Bolinha" style={{ width: '100%' }} />
                </div>
                <div style={{ flex: 1, minWidth: '160px' }}>
                  <label style={{ display: 'block', fontSize: 'var(--text-13)', marginBottom: '0.3rem' }}>Data de nascimento *</label>
                  <input className="form-input" type="date" value={petBirthDate} onChange={e => setPetBirthDate(e.target.value)} style={{ width: '100%' }} />
                </div>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
                Os dados do pet serão salvos no cadastro do cliente.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
            <button className="btn btn-ghost" onClick={() => setStep(1)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <ChevronLeft size={16} /> Voltar
            </button>
            <button
              className="btn btn-primary"
              disabled={!canGenerate() || generating}
              onClick={handleGenerate}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '200px', justifyContent: 'center' }}
            >
              {generating ? (
                <>
                  <span className="spinner" style={{ width: '16px', height: '16px' }} />
                  Gerando análise...
                </>
              ) : (
                <>Gerar análise para {selectedClient?.name?.split(' ')[0]}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Result ── */}
      {step === 3 && result && (
        <div>
          {/* Sticky download bar */}
          <div style={{
            position: 'sticky', top: 0, zIndex: 10,
            background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)',
            padding: '0.875rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem',
            flexWrap: 'wrap', marginBottom: '1.5rem',
            borderRadius: '12px 12px 0 0', boxShadow: 'var(--shadow)',
          }}>
            <div style={{ flex: 1, minWidth: '0' }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-15)', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Análise para {selectedClient?.name}, {selectedService?.name}
              </p>
              <p style={{ margin: 0, fontSize: 'var(--text-13)', color: 'var(--text-muted)' }}>
                {new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
            <button
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}
              disabled={downloading === 'pdf'}
              onClick={handleDownloadPdf}
            >
              {downloading === 'pdf' ? <span className="spinner" style={{ width: '14px', height: '14px' }} /> : <Download size={15} />}
              Baixar PDF
            </button>
            <button
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}
              disabled={downloading === 'docx'}
              onClick={handleDownloadDocx}
            >
              {downloading === 'docx' ? <span className="spinner" style={{ width: '14px', height: '14px' }} /> : <FileText size={15} />}
              Baixar Word
            </button>
            <button className="btn btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={handleReset}>
              <RotateCcw size={15} /> Nova análise
            </button>
          </div>

          {/* Analysis content */}
          <div style={{
            background: 'var(--bg-surface)', borderRadius: '0 0 12px 12px',
            border: '1px solid var(--border-subtle)', borderTop: 'none',
            padding: '2rem', boxShadow: 'var(--shadow)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-18)', lineHeight: 1.8, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
              {contentText || <span style={{ color: 'var(--text-muted)' }}>Carregando conteúdo...</span>}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
